import { supabase } from '@/lib/supabase';
import { SeoTask } from '@/types/api/seo';

function mapRow(row: any): SeoTask {
  return {
    id: row.id,
    projectId: row.project_id,
    keywordId: row.keyword_id,
    opportunityId: row.opportunity_id,
    pageId: row.page_id,
    cycleId: row.cycle_id,
    agentId: row.agent_id ?? null,
    source: row.source ?? 'manual',
    title: row.title,
    description: row.description,
    taskType: row.task_type,
    suggestedValue: row.suggested_value,
    qualityScore: row.quality_score != null ? Number(row.quality_score) : null,
    status: row.status,
    priority: row.priority,
    appliedToWp: row.applied_to_wp,
    appliedAt: row.applied_at,
    createdBy: row.created_by,
    assignedTo: row.assigned_to,
    dueDate: row.due_date,
    completedAt: row.completed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const seoTasksService = {
  async getByKeyword(keywordId: string): Promise<SeoTask[]> {
    const { data, error } = await supabase
      .from('seo_tasks')
      .select('*')
      .eq('keyword_id', keywordId)
      .neq('status', 'dismissed')
      .order('priority', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getByProject(projectId: string): Promise<SeoTask[]> {
    const { data, error } = await supabase
      .from('seo_tasks')
      .select('*')
      .eq('project_id', projectId)
      .neq('status', 'dismissed')
      .order('status', { ascending: true })
      .order('priority', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async create(params: {
    projectId: string;
    keywordId?: string | null;
    opportunityId?: string | null;
    pageId?: string | null;
    title: string;
    description?: string;
    taskType: SeoTask['taskType'];
    suggestedValue?: string;
    priority?: SeoTask['priority'];
  }): Promise<SeoTask> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('seo_tasks')
      .insert({
        project_id: params.projectId,
        keyword_id: params.keywordId ?? null,
        opportunity_id: params.opportunityId ?? null,
        page_id: params.pageId ?? null,
        title: params.title,
        description: params.description ?? null,
        task_type: params.taskType,
        suggested_value: params.suggestedValue ?? null,
        priority: params.priority ?? 'medium',
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return mapRow(data);
  },

  async updateStatus(id: string, status: SeoTask['status']): Promise<void> {
    const updates: Record<string, any> = {
      status,
      updated_at: new Date().toISOString(),
    };
    if (status === 'done') {
      updates.completed_at = new Date().toISOString();
    }

    const { error } = await supabase.from('seo_tasks').update(updates).eq('id', id);

    if (error) throw error;
  },

  async markAppliedToWp(id: string): Promise<void> {
    const { error } = await supabase
      .from('seo_tasks')
      .update({
        applied_to_wp: true,
        applied_at: new Date().toISOString(),
        status: 'done',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) throw error;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('seo_tasks')
      .update({ status: 'dismissed', updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async bulkDelete(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from('seo_tasks')
      .update({ status: 'dismissed', updated_at: new Date().toISOString() })
      .in('id', ids);

    if (error) throw error;
  },
};

export default seoTasksService;
